import type {Draft, PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'

import {restApi} from '../../../../services/rest'
import {submitPager} from '../Pager.actions.base'
import type {Pager} from '../Pager.types'
import {PagerGroup} from '../Pager.types'
import {needsLoadMore} from '../Pager.utils'

const defaultPager: Pager = {
  precountedPages: 4,
  pageSize: 50,
  total: 0,
  openTotal: false,
  hasMore: true,
  isClientFilter: false,
  lookupLimit: 50000,
  lookupLimitWarning: false,
  lookupDelta: 50000,
  loadedLessThanRequested: null,
}

const pager = createSlice({
  name: 'buildPager',
  initialState: defaultPager,
  reducers: {
    changeCurrentPage(state, action: PayloadAction<number>) {
      const {lookupLimit = 0, lookupDelta = 0, lookupLimitWarning: warning} = state
      const updateLimit = warning === true && needsLoadMore(action.payload, state)
      state.lookupLimit = updateLimit ? lookupLimit + lookupDelta : lookupLimit
    },
    incrementLookupLimit(state) {
      const {lookupLimit = 0, lookupDelta = 0} = state
      state.lookupLimit = lookupLimit + lookupDelta
    },
  },
  extraReducers: builder => {
    function reset(state: Draft<Pager>) {
      state.total = 0
      state.openTotal = false
      state.lookupLimitWarning = false
      state.isLoading = false
    }
    builder.addCase(submitPager, (state, action) => {
      if (action.meta.group === PagerGroup.BUILD) {
        Object.assign(state, action.payload)
      }
    })
    builder.addMatcher(restApi.endpoints.getAllBuilds.matchPending, (state, action) => {
      const {inBackground} = action.meta.arg.originalArgs
      if (!inBackground) {
        state.isLoading = true
      }
    })
    builder.addMatcher(restApi.endpoints.getAllBuilds.matchFulfilled, (state, action) => {
      const {count = 0} = action.meta.arg.originalArgs
      const actionData = action.payload

      if (actionData == null) {
        reset(state)
        return
      }

      const hasMore = actionData.nextHref != null
      const total = actionData.count ?? 0
      const loadedLessThanRequested = total < count
      const lookupLimitWarning = loadedLessThanRequested && hasMore
      state.total = state.isClientFilter ? state.total : total
      state.lookupLimitWarning = lookupLimitWarning
      state.hasMore = hasMore
      state.openTotal = hasMore && !loadedLessThanRequested
      state.isLoading = false
    })
    builder.addMatcher(restApi.endpoints.getAllBuilds.matchRejected, (state, action) => {
      if (action.meta.condition) {
        // request was aborted due to condition (another query already running)
        return
      }
      reset(state)
    })
  },
})

export default pager
